import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import ReactMarkdown from 'react-markdown';
import apiService from '../services/api';
import { formatNumberForChart } from '../utils/formatNumber';

// Helper function to parse AI summary into sections
const parseAiSummary = (summary) => {
  if (!summary) return { insights: '', trends: '', risks: '', actions: '' };
  
  // Split by the section headers
  const insightsMatch = summary.match(/###\s*Key Insights[\s\S]*?(?=###|$)/i);
  const trendsMatch = summary.match(/###\s*Positive Trends[\s\S]*?(?=###|$)/i);
  const risksMatch = summary.match(/###\s*Risks[^\n]*[\s\S]*?(?=###|$)/i);
  const actionsMatch = summary.match(/###\s*Recommended Actions[\s\S]*?(?=###|$)/i);
  
  return {
    insights: insightsMatch ? insightsMatch[0].replace(/###\s*Key Insights\s*/i, '').trim() : '',
    trends: trendsMatch ? trendsMatch[0].replace(/###\s*Positive Trends\s*/i, '').trim() : '',
    risks: risksMatch ? risksMatch[0].replace(/###\s*Risks[^\n]*\s*/i, '').trim() : '',
    actions: actionsMatch ? actionsMatch[0].replace(/###\s*Recommended Actions\s*/i, '').trim() : ''
  };
};

const AreaDetails = () => {
  const { id } = useParams();
  const [area, setArea] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [department, setDepartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [aiSummaryError, setAiSummaryError] = useState(null);
  const [aiSummaryExpanded, setAiSummaryExpanded] = useState(false);
  const [chartViewType, setChartViewType] = useState('monthly'); // 'last10', 'monthly', 'quarterly', 'lastmonth', 'last3months'

  const fetchAreaDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch area details
      const areaResponse = await apiService.areas.getById(id);
      setArea(areaResponse.data);

      // Fetch department for this area
      if (areaResponse.data.department_id) {
        try {
          const departmentResponse = await apiService.departments.getById(areaResponse.data.department_id);
          setDepartment(departmentResponse.data);
        } catch (err) {
          console.error('Error fetching department:', err);
        }
      }

      // Fetch all metrics for this area
      const metricsResponse = await apiService.metrics.getAll({
        area_id: id
      });
      setMetrics(metricsResponse.data);

    } catch (err) {
      setError('Failed to load area details');
      console.error('Error fetching area details:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchAiSummary = useCallback(async (forceRefresh = false) => {
    if (!id || metrics.length === 0) return;
    try {
      setAiSummaryLoading(true);
      setAiSummaryError(null);
      
      // Calculate date range (90 days ago)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 90);
      
      // Fetch metric values for all metrics in this area
      const metricsWithValues = [];
      for (const metric of metrics) {
        try {
          const valuesResponse = await apiService.metricValues.getAll({
            metric_id: metric.id,
            date_from: startDate.toISOString().split('T')[0],
            date_to: endDate.toISOString().split('T')[0],
            page: 1,
            page_size: 1000,
            sort_by: 'value_date',
            sort_dir: 'asc'
          });
          const values = valuesResponse.data.items || [];
          if (values.length > 0) {
            metricsWithValues.push({
              metric: metric,
              values: values
            });
          }
        } catch (err) {
          console.error(`Error fetching values for metric ${metric.id}:`, err);
        }
      }
      
      if (metricsWithValues.length === 0) {
        setAiSummary({
          summary: "No metric values found in the last 90 days for this area.",
          generated_at: new Date().toISOString(),
          cached: false
        });
        return;
      }
      
      // Call area-specific AI summary endpoint
      const response = await apiService.areas.getAiSummary(id, forceRefresh);
      setAiSummary(response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to load AI summary';
      setAiSummaryError(errorMessage);
      console.error('AI Summary error:', err);
    } finally {
      setAiSummaryLoading(false);
    }
  }, [id, metrics]);

  useEffect(() => {
    fetchAreaDetails();
  }, [fetchAreaDetails]);

  useEffect(() => {
    if (metrics.length > 0) {
      fetchAiSummary();
    }
  }, [metrics, fetchAiSummary]);

  // Fetch metric values for each metric to create charts
  const [metricValues, setMetricValues] = useState({});
  const [chartsLoading, setChartsLoading] = useState(false);

  useEffect(() => {
    const fetchMetricValues = async () => {
      if (metrics.length === 0) return;
      
      setChartsLoading(true);
      try {
        const currentYear = new Date().getFullYear();
        const yearStart = `${currentYear}-01-01`;
        const today = new Date().toISOString().split('T')[0];
        
        const valuesPromises = metrics.map(async (metric) => {
          try {
            // For monthly/quarterly, get all values for current year
            // For last10, get last 10 values
            // For lastmonth, get all values from previous month
            // For last3months, get all values from last 3 months
            let params;
            if (chartViewType === 'last10') {
              params = {
                metric_id: metric.id,
                page: 1,
                page_size: 10,
                sort_by: 'value_date',
                sort_dir: 'desc'
              };
            } else if (chartViewType === 'lastmonth') {
              // Calculate previous month date range
              const now = new Date();
              const firstDayOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
              const lastDayOfPreviousMonth = new Date(firstDayOfCurrentMonth.getTime() - 1);
              const firstDayOfPreviousMonth = new Date(lastDayOfPreviousMonth.getFullYear(), lastDayOfPreviousMonth.getMonth(), 1);
              
              const monthStart = firstDayOfPreviousMonth.toISOString().split('T')[0];
              const monthEnd = lastDayOfPreviousMonth.toISOString().split('T')[0];
              
              params = {
                metric_id: metric.id,
                date_from: monthStart,
                date_to: monthEnd,
                sort_by: 'value_date',
                sort_dir: 'asc',
                page: 1,
                page_size: 10000 // Large page size to get all values
              };
            } else if (chartViewType === 'last3months') {
              // Calculate last 3 months date range
              const now = new Date();
              const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, 1);
              
              const threeMonthsStart = threeMonthsAgo.toISOString().split('T')[0];
              const todayStr = now.toISOString().split('T')[0];
              
              params = {
                metric_id: metric.id,
                date_from: threeMonthsStart,
                date_to: todayStr,
                sort_by: 'value_date',
                sort_dir: 'asc',
                page: 1,
                page_size: 10000 // Large page size to get all values
              };
            } else {
              params = {
                metric_id: metric.id,
                date_from: yearStart,
                date_to: today,
                sort_by: 'value_date',
                sort_dir: 'asc',
                page: 1,
                page_size: 10000 // Large page size to get all values
              };
            }
            
            const response = await apiService.metricValues.getAll(params);
            return { metricId: metric.id, values: response.data.items || [] };
          } catch (err) {
            console.error(`Error fetching values for metric ${metric.id}:`, err);
            return { metricId: metric.id, values: [] };
          }
        });

        const results = await Promise.all(valuesPromises);
        const valuesMap = {};
        results.forEach(({ metricId, values }) => {
          valuesMap[metricId] = values;
        });
        setMetricValues(valuesMap);
      } catch (err) {
        console.error('Error fetching metric values:', err);
      } finally {
        setChartsLoading(false);
      }
    };

    fetchMetricValues();
  }, [metrics, chartViewType]);

  // Aggregate values based on aggregation type
  const aggregateValues = (values, aggregationType) => {
    if (!values || values.length === 0) return null;
    const numericValues = values.filter(v => v !== null && v !== undefined && !isNaN(v)).map(v => parseFloat(v));
    if (numericValues.length === 0) return null;
    
    switch (aggregationType?.toLowerCase()) {
      case 'sum':
        return numericValues.reduce((sum, val) => sum + val, 0);
      case 'average':
      case 'avg':
        return numericValues.reduce((sum, val) => sum + val, 0) / numericValues.length;
      case 'count':
        return numericValues.length;
      case 'min':
        return Math.min(...numericValues);
      case 'max':
        return Math.max(...numericValues);
      case 'cumulative':
        // For cumulative metrics, take the last (maximum) value in the period
        // since cumulative values only increase over time
        return Math.max(...numericValues);
      default:
        // Default to sum if aggregation type not specified
        return numericValues.reduce((sum, val) => sum + val, 0);
    }
  };

  // Get last day of month
  const getLastDayOfMonth = (year, month) => {
    return new Date(year, month + 1, 0);
  };

  // Get last day of quarter
  const getLastDayOfQuarter = (year, quarter) => {
    const month = (quarter - 1) * 3 + 2; // Last month of quarter (0-indexed: 2, 5, 8, 11)
    return new Date(year, month + 1, 0);
  };

  // Prepare monthly aggregated chart data
  const getMonthlyChartData = (metric, values) => {
    const currentYear = new Date().getFullYear();
    const monthlyData = {};
    const isCumulative = metric?.aggregation_type?.toLowerCase() === 'cumulative';
    
    // Filter to current year and group by month
    values.forEach(value => {
      const date = new Date(value.value_date);
      if (date.getFullYear() !== currentYear) return;
      
      const month = date.getMonth(); // 0-11
      const monthKey = `${currentYear}-${String(month + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          year: currentYear,
          month: month,
          values: [],
          targets: [],
          valueObjects: [], // Store full objects for cumulative metrics
          targetObjects: []
        };
      }
      
      if (value.value !== null && value.value !== undefined) {
        monthlyData[monthKey].values.push(parseFloat(value.value));
        if (isCumulative) {
          monthlyData[monthKey].valueObjects.push({
            value: parseFloat(value.value),
            date: date
          });
        }
      }
      
      if (value.target !== null && value.target !== undefined) {
        monthlyData[monthKey].targets.push(parseFloat(value.target));
        if (isCumulative) {
          monthlyData[monthKey].targetObjects.push({
            value: parseFloat(value.target),
            date: date
          });
        }
      }
    });
    
    // Convert to chart data
    const chartData = Object.keys(monthlyData)
      .sort()
      .map(monthKey => {
        const month = monthlyData[monthKey];
        const lastDay = getLastDayOfMonth(month.year, month.month);
        const periodLabel = lastDay.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        
        let actual, target;
        if (isCumulative) {
          // For cumulative, take the last value by date in the period
          const sortedValues = month.valueObjects.sort((a, b) => a.date - b.date);
          const sortedTargets = month.targetObjects.sort((a, b) => a.date - b.date);
          actual = sortedValues.length > 0 ? sortedValues[sortedValues.length - 1].value : null;
          target = sortedTargets.length > 0 ? sortedTargets[sortedTargets.length - 1].value : null;
        } else {
          actual = aggregateValues(month.values, metric.aggregation_type);
          target = aggregateValues(month.targets, metric.aggregation_type);
        }
        
        return {
          date: lastDay.toLocaleDateString(),
          dateLabel: periodLabel,
          actual: actual,
          target: target
        };
      });
    
    return chartData;
  };

  // Prepare quarterly aggregated chart data
  const getQuarterlyChartData = (metric, values) => {
    const currentYear = new Date().getFullYear();
    const quarterlyData = {};
    const isCumulative = metric?.aggregation_type?.toLowerCase() === 'cumulative';
    
    // Filter to current year and group by quarter
    values.forEach(value => {
      const date = new Date(value.value_date);
      if (date.getFullYear() !== currentYear) return;
      
      const quarter = Math.floor(date.getMonth() / 3) + 1; // 1-4
      const quarterKey = `${currentYear}-Q${quarter}`;
      
      if (!quarterlyData[quarterKey]) {
        quarterlyData[quarterKey] = {
          year: currentYear,
          quarter: quarter,
          values: [],
          targets: [],
          valueObjects: [], // Store full objects for cumulative metrics
          targetObjects: []
        };
      }
      
      if (value.value !== null && value.value !== undefined) {
        quarterlyData[quarterKey].values.push(parseFloat(value.value));
        if (isCumulative) {
          quarterlyData[quarterKey].valueObjects.push({
            value: parseFloat(value.value),
            date: date
          });
        }
      }
      
      if (value.target !== null && value.target !== undefined) {
        quarterlyData[quarterKey].targets.push(parseFloat(value.target));
        if (isCumulative) {
          quarterlyData[quarterKey].targetObjects.push({
            value: parseFloat(value.target),
            date: date
          });
        }
      }
    });
    
    const chartData = Object.keys(quarterlyData)
      .sort()
      .map(quarterKey => {
        const quarter = quarterlyData[quarterKey];
        const lastDay = getLastDayOfQuarter(quarter.year, quarter.quarter);
        const periodLabel = `Q${quarter.quarter} ${quarter.year}`;
        
        let actual, target;
        if (isCumulative) {
          // For cumulative, take the last value by date in the period
          const sortedValues = quarter.valueObjects.sort((a, b) => a.date - b.date);
          const sortedTargets = quarter.targetObjects.sort((a, b) => a.date - b.date);
          actual = sortedValues.length > 0 ? sortedValues[sortedValues.length - 1].value : null;
          target = sortedTargets.length > 0 ? sortedTargets[sortedTargets.length - 1].value : null;
        } else {
          actual = aggregateValues(quarter.values, metric.aggregation_type);
          target = aggregateValues(quarter.targets, metric.aggregation_type);
        }
        
        return {
          date: lastDay.toLocaleDateString(),
          dateLabel: periodLabel,
          actual: actual,
          target: target
        };
      });
    
    return chartData;
  };

  // Prepare chart data for each metric
  const getChartData = (metric) => {
    const values = metricValues[metric.id] || [];
    
    if (chartViewType === 'monthly') {
      return getMonthlyChartData(metric, values);
    } else if (chartViewType === 'quarterly') {
      return getQuarterlyChartData(metric, values);
    } else if (chartViewType === 'lastmonth') {
      // Last month - show all values from previous month
      return values.map(value => ({
        date: new Date(value.value_date).toLocaleDateString(),
        dateLabel: new Date(value.value_date).toLocaleDateString(),
        actual: parseFloat(value.value),
        target: value.target ? parseFloat(value.target) : null
      }));
    } else if (chartViewType === 'last3months') {
      // Last 3 months - show all values from last 3 months
      return values.map(value => ({
        date: new Date(value.value_date).toLocaleDateString(),
        dateLabel: new Date(value.value_date).toLocaleDateString(),
        actual: parseFloat(value.value),
        target: value.target ? parseFloat(value.target) : null
      }));
    } else {
      // Last 10 values
      return values.slice(0, 10).map(value => ({
        date: new Date(value.value_date).toLocaleDateString(),
        dateLabel: new Date(value.value_date).toLocaleDateString(),
        actual: parseFloat(value.value),
        target: value.target ? parseFloat(value.target) : null
      })).reverse(); // Reverse to show oldest to newest
    }
  };

  // Helper function to determine if a value is on the wrong side of the target
  const isValueOnWrongSide = (actual, target, higherIsBetter) => {
    if (actual === null || actual === undefined || target === null || target === undefined) {
      return false;
    }
    if (higherIsBetter) {
      // Higher is better: wrong side if actual < target
      return actual < target;
    } else {
      // Lower is better: wrong side if actual > target
      return actual > target;
    }
  };

  // Calculate smart Y-axis domain to handle negative values
  const getYAxisDomain = (chartData) => {
    if (!chartData || chartData.length === 0) {
      return ['auto', 'auto'];
    }

    // Extract all numeric values (actual and target)
    const allValues = chartData
      .flatMap(d => [d.actual, d.target])
      .filter(v => v !== null && v !== undefined && !isNaN(v));

    if (allValues.length === 0) {
      return ['auto', 'auto'];
    }

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);

    // If all values are negative, include 0 at the top
    if (max < 0) {
      return [min * 1.1, 0]; // Add 10% padding below minimum
    }

    // If values span negative and positive, include 0 in the middle
    if (min < 0 && max > 0) {
      const range = Math.max(Math.abs(min), Math.abs(max));
      return [min - range * 0.1, max + range * 0.1]; // Add 10% padding on both sides
    }

    // Default behavior for all positive values
    return ['auto', 'auto'];
  };

  if (loading) {
    return <div className="loading">Loading area details...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!area) {
    return <div className="error">Area not found</div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/areas" className="btn btn-secondary btn-small">
            ← Back to Areas
          </Link>
          <h1 className="page-title">{area.name}</h1>
          <p className="text-muted">Area Details</p>
        </div>
      </div>

      {/* Area Information - Horizontal Layout */}
      <div className="details-card" style={{ marginBottom: '2rem' }}>
        <h3>Area Information</h3>
        <div style={{ 
          display: 'flex', 
          gap: '2rem', 
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <strong>Name:</strong>
            <span>{area.name}</span>
          </div>
          {department && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <strong>Department:</strong>
              <Link to={`/departments/${department.id}`} style={{ color: '#034955', textDecoration: 'none' }}>
                {department.name}
              </Link>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <strong>Created:</strong>
            <span>{new Date(area.created_at).toLocaleDateString()}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <strong>Metrics Count:</strong>
            <span>{metrics.length}</span>
          </div>
        </div>
      </div>

      {/* Performance Charts for each metric */}
      {metrics.length > 0 && (
        <div className="details-card" style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>Performance Charts</h3>
            <select
              value={chartViewType}
              onChange={(e) => setChartViewType(e.target.value)}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '1rem',
                borderRadius: '4px',
                border: '1px solid #ccc',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="last10">Last 10 Values</option>
              <option value="lastmonth">Last Month</option>
              <option value="last3months">Last 3 Months</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </div>
            {chartsLoading ? (
              <div className="loading">Loading charts...</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem' }}>
                {metrics.map(metric => {
                  const chartData = getChartData(metric);
                  return (
                    <div key={metric.id} style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '1rem' }}>
                      <Link 
                        to={`/metrics/${metric.id}`}
                        style={{ 
                          textDecoration: 'none',
                          display: 'block'
                        }}
                      >
                        <h4 style={{ 
                          marginBottom: '1rem', 
                          color: '#034955',
                          cursor: 'pointer',
                          transition: 'color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#032E36'}
                        onMouseLeave={(e) => e.target.style.color = '#034955'}
                        >
                          {metric.name}
                        </h4>
                      </Link>
                      {chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey={chartViewType === 'last10' || chartViewType === 'lastmonth' || chartViewType === 'last3months' ? 'date' : 'dateLabel'} />
                            <YAxis 
                              domain={getYAxisDomain(chartData)}
                              tickFormatter={(value) => formatNumberForChart(value, metric.display_format || 'number', metric.unit)}
                            />
                            <Tooltip 
                              formatter={(value, name) => {
                                const label = name === 'Actual' ? 'Actual' : 'Target';
                                const formattedValue = formatNumberForChart(
                                  value, 
                                  metric.display_format || 'number',
                                  metric.unit
                                );
                                return [formattedValue, label];
                              }}
                            />
                            <Legend />
                            <Bar dataKey="actual" name="Actual">
                              {chartData.map((entry, index) => {
                                const isWrongSide = isValueOnWrongSide(
                                  entry.actual,
                                  entry.target,
                                  metric.higher_is_better !== false
                                );
                                return (
                                  <Cell key={`cell-${index}`} fill={isWrongSide ? '#DC3545' : '#034955'} />
                                );
                              })}
                            </Bar>
                            <Line 
                              type="monotone" 
                              dataKey="target" 
                              stroke="#DE37A4" 
                              strokeWidth={3}
                              dot={{ fill: '#DE37A4', strokeWidth: 2, r: 4 }}
                              name="Target" 
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-muted">No data available for chart</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      )}

      {/* AI Summary Section - Below Charts */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>AI-Powered Area Summary</h3>
          <button 
            className="btn btn-secondary btn-small"
            onClick={() => fetchAiSummary(true)}
            disabled={aiSummaryLoading}
            style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
          >
            {aiSummaryLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <div style={{ padding: '1.5rem' }}>
          {aiSummaryLoading && !aiSummary && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
              Generating AI summary...
            </div>
          )}
          {aiSummaryError && !aiSummary && (
            <div className="error" style={{ 
              margin: 0, 
              padding: '1rem',
              background: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              color: '#721c24'
            }}>
              <strong>Error loading AI summary:</strong><br />
              {aiSummaryError}
            </div>
          )}
          {aiSummary && (() => {
            const sections = parseAiSummary(aiSummary.summary);
            return (
              <div>
                {aiSummary.warning && (
                  <div className="warning-banner" style={{ marginBottom: '1rem' }}>
                    {aiSummary.warning}
                  </div>
                )}
                {aiSummaryExpanded && (
                  <div className="ai-summary-four-column" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '1.5rem',
                    marginBottom: '1rem'
                  }}>
                    {/* Key Insights Column */}
                    <div style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      padding: '1rem',
                      backgroundColor: '#f8f9fa'
                    }}>
                      <h4 style={{
                        margin: '0 0 0.75rem 0',
                        fontSize: '1rem',
                        fontWeight: 700,
                        color: '#032E36',
                        borderBottom: '2px solid #034955',
                        paddingBottom: '0.5rem'
                      }}>
                        Key Insights
                      </h4>
                      <div className="ai-summary-content" style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#032E36' }}>
                        <ReactMarkdown>{sections.insights}</ReactMarkdown>
                      </div>
                    </div>
                    
                    {/* Positive Trends Column */}
                    <div style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      padding: '1rem',
                      backgroundColor: '#f0fff4'
                    }}>
                      <h4 style={{
                        margin: '0 0 0.75rem 0',
                        fontSize: '1rem',
                        fontWeight: 700,
                        color: '#032E36',
                        borderBottom: '2px solid #28a745',
                        paddingBottom: '0.5rem'
                      }}>
                        Positive Trends
                      </h4>
                      <div className="ai-summary-content" style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#032E36' }}>
                        <ReactMarkdown>{sections.trends}</ReactMarkdown>
                      </div>
                    </div>
                    
                    {/* Risks & Deviations Column */}
                    <div style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      padding: '1rem',
                      backgroundColor: '#fff'
                    }}>
                      <h4 style={{
                        margin: '0 0 0.75rem 0',
                        fontSize: '1rem',
                        fontWeight: 700,
                        color: '#032E36',
                        borderBottom: '2px solid #dc3545',
                        paddingBottom: '0.5rem'
                      }}>
                        Risks & Deviations
                      </h4>
                      <div className="ai-summary-content" style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#032E36' }}>
                        <ReactMarkdown>{sections.risks}</ReactMarkdown>
                      </div>
                    </div>
                    
                    {/* Recommended Actions Column */}
                    <div style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      padding: '1rem',
                      backgroundColor: '#f0f8ff'
                    }}>
                      <h4 style={{
                        margin: '0 0 0.75rem 0',
                        fontSize: '1rem',
                        fontWeight: 700,
                        color: '#032E36',
                        borderBottom: '2px solid #007bff',
                        paddingBottom: '0.5rem'
                      }}>
                        Recommended Actions
                      </h4>
                      <div className="ai-summary-content" style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#032E36' }}>
                        <ReactMarkdown>{sections.actions}</ReactMarkdown>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Footer with generation info and toggle */}
                <div style={{ 
                  marginTop: '1rem', 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '0.875rem', 
                  color: '#6c757d',
                  borderTop: '1px solid #e0e0e0',
                  paddingTop: '1rem'
                }}>
                  <span>
                    Generated: {new Date(aiSummary.generated_at).toLocaleString()}
                    {aiSummary.cached && ' (Cached)'}
                  </span>
                  <button
                    className="btn btn-link"
                    onClick={() => setAiSummaryExpanded(!aiSummaryExpanded)}
                    style={{
                      fontSize: '0.875rem',
                      padding: '0.25rem 0.5rem',
                      textDecoration: 'none',
                      color: '#034955',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none'
                    }}
                  >
                    {aiSummaryExpanded ? 'Show Less' : 'Show More'}
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default AreaDetails;

